import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, RefreshCw } from 'lucide-react';
import Modal from '@mui/material/Modal';

import useAccessPointApi, { DIRECTION_NONE } from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const QrModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  min-width: 340px;
  box-shadow: 0 8px 40px rgba(0, 66, 66, 0.2);
  outline: none;
`;
const QrHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const QrTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;
const CloseBtn = styled.button`
  border: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;
const QrBody = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px;
`;
const QrName = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;
const TokenRow = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.light};
  text-align: center;
  word-break: break-all;
  padding: 0 8px;
`;
const QrActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;
const EmptyQr = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.light};
  font-size: 13px;
`;

export default function AccessPointsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, regenerateQr } = useAccessPointApi(queryParams);
  const [qrRow, setQrRow] = useState(null); // row to show QR for (updated with fresh token on regen)
  const [regenerating, setRegenerating] = useState(false);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    // DB stores null for "Normal Geçiş" — surface it as 'gate' so the select prefills.
    // Okuyucu yönü null (tahmin edilir) → 'none' gözcüsü; gövdede yine null gider (toAccessPointPayload).
    setSelectedRow({ ...row, purpose: row.purpose || 'gate', direction: row.direction || DIRECTION_NONE });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('accessPoint.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    const opts = [
      { type: 'details', label: t('accessPoint.showQr'), onClick: (row) => setQrRow(row) },
    ];
    if (canWrite('AC')) {
      opts.push(
        { type: 'divider' },
        { type: 'edit', label: t('accessPoint.edit'), onClick: handleEdit },
        { type: 'delete', label: t('accessPoint.delete'), onClick: handleDelete },
      );
    }
    return opts;
  }, [t, canWrite, handleEdit, handleDelete]);

  const handleRegenerate = useCallback(async () => {
    if (!qrRow) return;
    setRegenerating(true);
    const newToken = await regenerateQr(qrRow.id);
    if (newToken) setQrRow({ ...qrRow, qrToken: newToken });
    setRegenerating(false);
  }, [qrRow, regenerateQr]);

  const handleDownloadQr = useCallback(() => {
    const svg = document.getElementById('ap-qr-code');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const link = document.createElement('a');
      link.download = `qr-${qrRow?.name || 'access-point'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
  }, [qrRow]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('accessPoint.edit') : t('accessPoint.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('accessPoint.deleteTitle')}
        message={t('accessPoint.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <Modal open={!!qrRow} onClose={() => setQrRow(null)}>
        <QrModalWrap>
          <QrHeader>
            <QrTitle>{t('accessPoint.qrCode')}</QrTitle>
            <CloseBtn onClick={() => setQrRow(null)}><X size={18} /></CloseBtn>
          </QrHeader>
          <QrBody>
            <QrName>{qrRow?.name}</QrName>
            {qrRow?.qrToken ? (
              <>
                <QRCodeSVG id="ap-qr-code" value={qrRow.qrToken} size={240} level="M" includeMargin />
                <TokenRow>{qrRow.qrToken}</TokenRow>
              </>
            ) : (
              <EmptyQr>{t('accessPoint.noQrYet')}</EmptyQr>
            )}
            <QrActions>
              {qrRow?.qrToken && (
                <Button variant="outline" onClick={handleDownloadQr}>
                  <Download size={14} /> {t('accessPoint.downloadQr')}
                </Button>
              )}
              {canWrite('AC') && (
                <Button onClick={handleRegenerate} disabled={regenerating}>
                  <RefreshCw size={14} /> {qrRow?.qrToken ? t('accessPoint.regenerateQr') : t('accessPoint.generateQr')}
                </Button>
              )}
            </QrActions>
          </QrBody>
        </QrModalWrap>
      </Modal>

      {useMemo(() => (
        <DataTable
          title={t('accessPoint.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('AC')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('AC') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
